// fixedIncome.js — Pure financial calculation utilities for fixed-income portfolio analytics

/**
 * Calculate accrued interest using actual/actual day-count convention.
 * @param {number} faceValue - Nominal/face value of the bond
 * @param {number} couponRate - Annual coupon rate (e.g. 0.12 for 12%)
 * @param {number} daysSinceLast - Days since last coupon payment
 * @param {number} daysInPeriod - Total days in the coupon period
 * @returns {number} Accrued interest amount
 */
export function calcAccruedInterest(faceValue, couponRate, daysSinceLast, daysInPeriod) {
  if (!daysInPeriod || daysInPeriod <= 0) return 0;
  return faceValue * couponRate * (daysSinceLast / daysInPeriod);
}

/**
 * Calculate present-value market price of a fixed-income instrument.
 * Uses simple discounted cash flow for remaining coupon + principal.
 * @param {number} faceValue - Face/par value
 * @param {number} couponRate - Annual coupon rate (decimal)
 * @param {number} ytm - Yield to maturity (decimal)
 * @param {number} daysToMaturity - Days remaining until maturity
 * @returns {number} Estimated market value
 */
export function calcMarketValue(faceValue, couponRate, ytm, daysToMaturity) {
  const years = daysToMaturity / 365;
  if (years <= 0) return faceValue;
  if (ytm <= 0) return faceValue * (1 + couponRate * years);

  const periods = Math.max(1, Math.ceil(years));
  const couponPayment = faceValue * couponRate;
  let pv = 0;

  for (let t = 1; t <= periods; t++) {
    const discountFactor = Math.pow(1 + ytm, -(t * years / periods));
    if (t < periods) {
      pv += couponPayment * (years / periods) * discountFactor;
    } else {
      pv += (faceValue + couponPayment * (years / periods)) * discountFactor;
    }
  }

  return pv;
}

/**
 * Calculate modified duration (sensitivity to yield changes).
 * @param {number} couponRate - Annual coupon rate (decimal)
 * @param {number} ytm - Yield to maturity (decimal)
 * @param {number} maturityYears - Years to maturity
 * @returns {number} Modified duration in years
 */
export function calcModifiedDuration(couponRate, ytm, maturityYears) {
  if (maturityYears <= 0) return 0;
  if (ytm <= 0) return maturityYears;

  const c = couponRate;
  const y = ytm;
  const n = maturityYears;

  // Macaulay duration approximation for annual coupon bonds
  const numerator = (1 + y) / y - ((1 + y) + n * (c - y)) / (c * (Math.pow(1 + y, n) - 1) + y);
  const macaulay = Math.max(numerator, 0) || n / (1 + y);
  const modified = macaulay / (1 + y);

  return Math.min(modified, maturityYears);
}

/**
 * Time-weighted return via geometric linking of period returns.
 * @param {number[]} periodReturns - Array of period returns (decimals, e.g. 0.02 for 2%)
 * @returns {number} Cumulative TWR (decimal)
 */
export function calcTWR(periodReturns) {
  if (!periodReturns || periodReturns.length === 0) return 0;
  const product = periodReturns.reduce((acc, r) => acc * (1 + r), 1);
  return product - 1;
}

/**
 * Money-weighted return (simple IRR proxy using weighted cash flow method).
 * @param {number[]} cashFlows - Array of cash flows (negative = outflow, positive = inflow)
 * @param {string[]} dates - Array of date strings corresponding to cash flows
 * @returns {number} Approximate annualized MWR (decimal)
 */
export function calcMWR(cashFlows, dates) {
  if (!cashFlows || cashFlows.length < 2) return 0;

  const parsedDates = dates.map(d => new Date(d).getTime());
  const totalDays = (parsedDates[parsedDates.length - 1] - parsedDates[0]) / (1000 * 60 * 60 * 24);
  if (totalDays <= 0) return 0;

  const totalInvested = cashFlows.filter(cf => cf < 0).reduce((s, cf) => s + Math.abs(cf), 0);
  const totalReceived = cashFlows.filter(cf => cf > 0).reduce((s, cf) => s + cf, 0);

  if (totalInvested === 0) return 0;

  const totalReturn = (totalReceived - totalInvested) / totalInvested;
  const annualized = Math.pow(1 + totalReturn, 365 / totalDays) - 1;

  return annualized;
}

/**
 * Adjust local-currency return for FX movements.
 * @param {number} localReturn - Local currency return (decimal)
 * @param {number} fxStart - FX rate at purchase (local per USD)
 * @param {number} fxEnd - FX rate now (local per USD)
 * @returns {number} USD-adjusted return (decimal)
 */
export function calcFxAdjustedReturn(localReturn, fxStart, fxEnd) {
  if (!fxStart || fxStart <= 0) return localReturn;
  const fxReturn = (fxStart / fxEnd) - 1; // positive if local strengthened
  return (1 + localReturn) * (1 + fxReturn) - 1;
}

/**
 * Group positions by country, summing face values and market values.
 * @param {Object[]} positions - Array of position objects
 * @returns {Object[]} Array of { country, faceValue, marketValue, count }
 */
export function groupByCountry(positions) {
  const map = {};
  (positions || []).forEach(p => {
    if (!map[p.country]) {
      map[p.country] = { country: p.country, faceValue: 0, marketValue: 0, count: 0 };
    }
    map[p.country].faceValue += p.faceValue || 0;
    map[p.country].marketValue += calcMarketValue(
      p.faceValue, p.couponRate || 0, p.yieldToMaturity || 0,
      daysBetween(new Date(), new Date(p.maturityDate))
    );
    map[p.country].count += 1;
  });
  return Object.values(map).sort((a, b) => b.marketValue - a.marketValue);
}

/**
 * Group positions by instrument type, summing face values and market values.
 * @param {Object[]} positions - Array of position objects
 * @returns {Object[]} Array of { instrument, faceValue, marketValue, count }
 */
export function groupByInstrument(positions) {
  const map = {};
  (positions || []).forEach(p => {
    const key = p.instrument || 'Unknown';
    if (!map[key]) {
      map[key] = { instrument: key, faceValue: 0, marketValue: 0, count: 0 };
    }
    map[key].faceValue += p.faceValue || 0;
    map[key].marketValue += calcMarketValue(
      p.faceValue, p.couponRate || 0, p.yieldToMaturity || 0,
      daysBetween(new Date(), new Date(p.maturityDate))
    );
    map[key].count += 1;
  });
  return Object.values(map).sort((a, b) => b.marketValue - a.marketValue);
}

/**
 * Calculate aggregate portfolio statistics.
 * @param {Object[]} positions - Array of position objects
 * @returns {Object} { totalFaceValue, totalMarketValue, weightedAvgYield, weightedAvgDuration, activeCount, maturingCount, unrealizedFxPnl }
 */
export function calcPortfolioStats(positions) {
  if (!positions || positions.length === 0) {
    return { totalFaceValue: 0, totalMarketValue: 0, weightedAvgYield: 0, weightedAvgDuration: 0, activeCount: 0, maturingCount: 0, unrealizedFxPnl: 0 };
  }

  let totalFaceValue = 0;
  let totalMarketValue = 0;
  let yieldWeightedSum = 0;
  let durationWeightedSum = 0;
  let activeCount = 0;
  let maturingCount = 0;
  let unrealizedFxPnl = 0;

  positions.forEach(p => {
    const daysRemaining = daysBetween(new Date(), new Date(p.maturityDate));
    const mv = calcMarketValue(p.faceValue, p.couponRate || 0, p.yieldToMaturity || 0, daysRemaining);
    const duration = calcModifiedDuration(p.couponRate || 0, p.yieldToMaturity || 0, daysRemaining / 365);

    totalFaceValue += p.faceValue || 0;
    totalMarketValue += mv;
    yieldWeightedSum += (p.yieldToMaturity || 0) * mv;
    durationWeightedSum += duration * mv;

    if (p.status === 'active') activeCount++;
    if (p.status === 'maturing') maturingCount++;

    // FX P&L: difference between purchase and current FX rate applied to face value in USD
    if (p.fxRateAtPurchase && p.currentFxRate && p.fxRateAtPurchase !== p.currentFxRate) {
      const usdAtPurchase = (p.faceValue || 0) / p.fxRateAtPurchase;
      const usdNow = (p.faceValue || 0) / p.currentFxRate;
      unrealizedFxPnl += usdNow - usdAtPurchase;
    }
  });

  return {
    totalFaceValue,
    totalMarketValue,
    weightedAvgYield: totalMarketValue > 0 ? yieldWeightedSum / totalMarketValue : 0,
    weightedAvgDuration: totalMarketValue > 0 ? durationWeightedSum / totalMarketValue : 0,
    activeCount,
    maturingCount,
    unrealizedFxPnl,
  };
}

/**
 * Calculate days between two dates.
 * @param {Date} from
 * @param {Date} to
 * @returns {number} Number of days (can be negative)
 */
export function daysBetween(from, to) {
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Format a number as compact currency string.
 * @param {number} value
 * @param {string} currency - e.g. 'USD'
 * @returns {string}
 */
export function formatCurrency(value, currency = 'USD') {
  if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
